import { useHaloUser } from '@/features/halocrm-user/hooks'
import { useVoivodeshipLabel } from '@/features/voivodeship/hooks'
import type { Customer, CustomerType } from '../types'
import { CustomerEditButton } from './CustomerEditButton'
import { CustomerDeactivateButton } from './CustomerDeactivateButton'

function HaloUserName({ userId }: { userId: number | null }) {
  const { data, isLoading, isError } = useHaloUser(userId)
  if (userId == null) return <>-</>
  if (isLoading) return <span className="text-muted-foreground">Ładowanie...</span>
  if (isError || !data) return <>#{userId} (nie odnaleziono)</>
  return (
    <>
      {data.fullName}
      {!data.isActive && <span className="ml-1 text-xs text-muted-foreground">(nieaktywny)</span>}
    </>
  )
}

interface RowProps {
  label: string
  value: React.ReactNode
}

function Row({ label, value }: RowProps) {
  return (
    <div className="flex items-start justify-between gap-4 border-b py-2 last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right break-all">{value ?? '-'}</span>
    </div>
  )
}

function identityRows(customer: Customer): RowProps[] {
  if (customer.type === 'PERSON') {
    return [
      { label: 'Imię', value: customer.firstName },
      { label: 'Nazwisko', value: customer.lastName },
      { label: 'PESEL', value: customer.pesel ?? '-' },
    ]
  }
  return [
    { label: 'Nazwa firmy', value: customer.name },
    { label: 'NIP', value: customer.nip ?? '-' },
    { label: 'REGON', value: customer.regon ?? '-' },
  ]
}

function typeLabel(t: CustomerType): string {
  return t === 'PERSON' ? 'Osoba fizyczna' : 'Firma'
}

export function CustomerGeneralTab({ customer }: { customer: Customer }) {
  const voivodeshipLabel = useVoivodeshipLabel()
  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-end gap-2">
        <CustomerEditButton customer={customer} />
        <CustomerDeactivateButton customer={customer} />
      </div>

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-2 text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Identyfikacja
        </h2>
        <Row label="Typ" value={typeLabel(customer.type)} />
        {identityRows(customer).map((r) => (
          <Row key={r.label} {...r} />
        ))}
      </section>

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-2 text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Klasyfikacja i przypisania
        </h2>
        <Row label="Typ projektu" value={customer.typeProjectCode ?? '-'} />
        <Row label="Województwo" value={voivodeshipLabel(customer.voivodeshipCode) ?? '-'} />
        <Row label="Opiekun" value={<HaloUserName userId={customer.assigneeUserId} />} />
        <Row label="Klient nadrzędny (parent_id)" value={customer.parentId ? `#${customer.parentId}` : '-'} />
      </section>

      {customer.note && (
        <section className="rounded-lg border bg-card p-4">
          <h2 className="mb-2 text-sm font-medium uppercase tracking-wide text-muted-foreground">
            Notatka
          </h2>
          <p className="text-sm whitespace-pre-wrap">{customer.note}</p>
        </section>
      )}

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-2 text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Metadane
        </h2>
        <Row label="ID" value={customer.id} />
        <Row label="Utworzono" value={new Date(customer.createdAt).toLocaleString('pl-PL')} />
        <Row label="Zmodyfikowano" value={new Date(customer.updatedAt).toLocaleString('pl-PL')} />
        <Row label="Utworzono przez" value={<HaloUserName userId={customer.createdByUserId} />} />
        <Row label="Zmodyfikowano przez" value={<HaloUserName userId={customer.updatedByUserId} />} />
      </section>
    </div>
  )
}
